/**
 * Authenticated API Client
 *
 * Provides a centralized way to make authenticated API requests with:
 * - Automatic token injection from useAccessToken()
 * - Retry logic with exponential backoff (max 3 attempts)
 * - Loading state management
 * - User-friendly error handling
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useAccessToken, useAuth } from '../auth/hooks';
import { getAuthConfig } from '../config/auth';
import type { ApiError, ApiRequestState } from './types';
import { mapErrorToUserMessage } from './errors';

/** Maximum number of retry attempts */
const MAX_RETRIES = 3;

/** Base delay for exponential backoff (ms) */
const BASE_DELAY = 1000;

/**
 * Get the current API URL.
 *
 * Checks localStorage for dev mode API target override,
 * otherwise uses the configured API URL.
 */
function getApiUrl(): string {
  const config = getAuthConfig();

  // Check for dev mode API target override
  if (import.meta.env.VITE_ENVIRONMENT === 'dev') {
    try {
      const stored = localStorage.getItem('vibe-dev-mode-config');
      if (stored) {
        const devConfig = JSON.parse(stored);
        if (devConfig.apiTarget === 'local') {
          return 'http://localhost:8000';
        }
      }
    } catch {
      // Ignore localStorage errors
    }
  }

  return config.apiUrl;
}

/**
 * Sleep for a given duration.
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Make an authenticated fetch request with retry logic.
 *
 * @param endpoint - API endpoint (relative to API base URL)
 * @param token - Access token for Authorization header
 * @param options - Additional fetch options
 * @param retryCount - Current retry attempt
 * @returns Response data or throws an error
 */
/**
 * Ensure endpoint has proper trailing slash to avoid 307 redirects.
 *
 * FastAPI routes like /clients redirect to /clients/ which can cause
 * issues with Authorization headers being dropped during redirect.
 */
function normalizeEndpoint(endpoint: string): string {
  // Split path and query string
  const [path, query] = endpoint.split('?');

  // If path already ends with /, leave it
  if (path.endsWith('/')) {
    return endpoint;
  }

  // Skip trailing slash for dastarkhan routes — they are defined without trailing slashes
  // and FastAPI will 307 redirect if we add one, dropping the Authorization header
  if (path.startsWith('/dastarkhan/')) {
    return endpoint;
  }

  // Skip for paths ending in a resource identifier (numeric ID, UUID, or named resource like "me")
  if (/\/[\w-]+$/.test(path) && /\/\d+$/.test(path)) {
    return endpoint;
  }

  // Add trailing slash to legacy collection endpoints (e.g., /clients -> /clients/)
  const normalizedPath = path + '/';
  return query ? `${normalizedPath}?${query}` : normalizedPath;
}

async function authenticatedFetch<T>(
  endpoint: string,
  token: string | null,
  options: RequestInit = {},
  retryCount = 0
): Promise<T> {
  const apiUrl = getApiUrl();
  const normalizedEndpoint = normalizeEndpoint(endpoint);
  const url = `${apiUrl}${normalizedEndpoint}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Add Authorization header if token is available
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Merge with any provided headers
  const finalHeaders = options.headers
    ? { ...headers, ...Object.fromEntries(new Headers(options.headers).entries()) }
    : headers;

  try {
    const response = await fetch(url, {
      ...options,
      headers: finalHeaders,
    });

    // Handle auth errors - don't retry
    if (response.status === 401 || response.status === 403) {
      const error: ApiError = {
        status: response.status,
        message: mapErrorToUserMessage(response.status),
        isAuthError: true,
        canRetry: false,
      };
      throw error;
    }

    // Handle other errors
    if (!response.ok) {
      // Try to extract the backend's detail message for better error reporting
      let serverMessage = '';
      try {
        const body = await response.json();
        serverMessage = body?.detail || '';
      } catch { /* response may not be JSON */ }

      const error: ApiError = {
        status: response.status,
        message: serverMessage || mapErrorToUserMessage(response.status),
        isAuthError: false,
        canRetry: retryCount < MAX_RETRIES - 1,
      };
      throw error;
    }

    // Handle empty responses (204 No Content, etc.)
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return undefined as T;
    }

    // Parse and return JSON response
    const data = await response.json();
    return data as T;
  } catch (error) {
    // If it's already an ApiError, handle retry logic
    if (isApiError(error)) {
      // Don't retry auth errors
      if (error.isAuthError) {
        throw error;
      }

      // Retry non-auth errors with exponential backoff
      if (retryCount < MAX_RETRIES - 1) {
        const delay = BASE_DELAY * Math.pow(2, retryCount);
        await sleep(delay);
        return authenticatedFetch<T>(endpoint, token, options, retryCount + 1);
      }

      throw error;
    }

    // Handle network errors
    if (error instanceof Error) {
      const networkError: ApiError = {
        status: 0,
        message: mapErrorToUserMessage(0, error.message),
        isAuthError: false,
        canRetry: retryCount < MAX_RETRIES - 1,
      };

      // Retry network errors
      if (retryCount < MAX_RETRIES - 1) {
        const delay = BASE_DELAY * Math.pow(2, retryCount);
        await sleep(delay);
        return authenticatedFetch<T>(endpoint, token, options, retryCount + 1);
      }

      throw networkError;
    }

    // Unknown error
    throw {
      status: 0,
      message: 'An unexpected error occurred.',
      isAuthError: false,
      canRetry: false,
    } as ApiError;
  }
}

/**
 * Type guard for ApiError.
 */
function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    'message' in error &&
    'isAuthError' in error
  );
}

/**
 * Hook for making authenticated API requests.
 *
 * Provides loading state, error handling, and automatic retry.
 *
 * @returns Request state and fetch function
 */
export function useAuthenticatedFetch<T>() {
  const token = useAccessToken();
  const auth = useAuth();
  const [state, setState] = useState<ApiRequestState<T>>({
    loading: false,
    error: null,
    data: null,
    retryCount: 0,
  });

  // Track if component is mounted
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetchData = useCallback(
    async (endpoint: string, options?: RequestInit) => {
      if (!isMounted.current) return;

      setState((prev) => ({
        ...prev,
        loading: true,
        error: null,
        retryCount: 0,
      }));

      try {
        const data = await authenticatedFetch<T>(endpoint, token, options);

        if (!isMounted.current) return;

        setState({
          loading: false,
          error: null,
          data,
          retryCount: 0,
        });

        return data;
      } catch (error) {
        if (!isMounted.current) return;

        const apiError = isApiError(error)
          ? error
          : {
              status: 0,
              message: 'An unexpected error occurred.',
              isAuthError: false,
              canRetry: false,
            };

        setState((prev) => ({
          loading: false,
          error: apiError,
          data: prev.data,
          retryCount: MAX_RETRIES,
        }));

        // Note: We intentionally don't auto-redirect on 401 errors.
        // This prevents redirect loops when the backend auth is misconfigured.
        // The error will be displayed to the user via the error state.

        throw apiError;
      }
    },
    [token, auth]
  );

  const reset = useCallback(() => {
    setState({
      loading: false,
      error: null,
      data: null,
      retryCount: 0,
    });
  }, []);

  return {
    ...state,
    fetchData,
    reset,
  };
}

/**
 * Hook for fetching data on mount.
 *
 * Automatically fetches data when the component mounts.
 *
 * @param endpoint - API endpoint to fetch
 * @param options - Fetch options
 * @returns Request state and refetch function
 */
export function useApiData<T>(endpoint: string, options?: RequestInit) {
  const { data, loading, error, retryCount, fetchData, reset } =
    useAuthenticatedFetch<T>();

  useEffect(() => {
    fetchData(endpoint, options);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint]);

  const refetch = useCallback(() => {
    fetchData(endpoint, options);
  }, [endpoint, options, fetchData]);

  return {
    data,
    loading,
    error,
    retryCount,
    refetch,
    reset,
  };
}
